// DocumentIconGallery.tsx
import * as React from "react";
import {
    Box, Paper, Stack, Typography, Tooltip, Checkbox,
    Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { DocumentDetails } from "../models/Document";


type Props = {
    documents: DocumentDetails[];
    dense?: boolean;
    initialSelection?: string[];
    onDeleteSelected?: (ids: string[]) => Promise<void> | void; // handled by hosting page
    onSelectionChange?: (ids: string[]) => void;                // optional
    hideDeleteConfirm?: boolean;                                // skip confirm dialog
    showDeleteButton: boolean | null;
};

const formatSize = (n?: number) =>
    typeof n === "number"
        ? n < 1024
            ? `${n} B`
            : n < 1024 ** 2
                ? `${(n / 1024).toFixed(1)} KB`
                : n < 1024 ** 3
                    ? `${(n / 1024 ** 2).toFixed(1)} MB`
                    : `${(n / 1024 ** 3).toFixed(1)} GB`
        : "";

const iconFor = (ct: string, title: string) => {
    const ext = title.toLowerCase();
    if (ct.includes("pdf") || ext.endsWith(".pdf")) return <PictureAsPdfIcon fontSize="inherit" />;
    if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/.test(ext)) return <ImageIcon fontSize="inherit" />;
    if (/word|msword|officedocument\.word/i.test(ct) || /\.docx?$/.test(ext)) return <DescriptionIcon fontSize="inherit" />;
    if (/sheet|excel|spreadsheet/i.test(ct) || /\.(xlsx?|csv)$/.test(ext)) return <DescriptionIcon fontSize="inherit" />;
    return <InsertDriveFileIcon fontSize="inherit" />;
};

function DocTile({
    doc,
    selected,
    onToggle,
    dense,
}: {
        doc: DocumentDetails;
    selected: boolean;
    onToggle: (e: React.MouseEvent | React.KeyboardEvent) => void;
    dense?: boolean;
}) {
    const size = dense ? 64 : 80;
    const iconSize = dense ? 28 : 32;

    const tooltip = (
        <Stack spacing={0.5} sx={{ maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
                {doc.title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'common.white' }}>
                ({doc.documentType})
            </Typography>
            {doc.description && (
                <Typography variant="caption" sx={{ color: 'common.white', opacity: 0.85 }}>
                    {doc.description}
                </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'common.white', opacity: 0.85 }}>
                {doc.sizeBytes ? `• ${formatSize(doc.sizeBytes)}` : ""}
            </Typography>
        </Stack>
    );

    return (
        <Tooltip
            title={tooltip}
            arrow
            enterDelay={250}
            slotProps={{
                tooltip: {
                    sx: (t) => ({
                        bgcolor: t.palette.grey[900],
                        color: t.palette.common.white, // default color if not overridden
                    }),
                },
                arrow: {
                    sx: (t) => ({ color: t.palette.grey[900] }),
                },
            }}
        >
            <Paper
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") onToggle(e); }}
                elevation={selected ? 3 : 1}
                sx={{
                    width: size, minWidth: size, height: size + 36,
                    p: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "flex-start", borderRadius: 2, cursor: "pointer",
                    position: "relative",
                    outline: (t) => (selected ? `2px solid ${t.palette.primary.main}` : "none"),
                }}
            >
                <Checkbox size="small" checked={selected} tabIndex={-1} sx={{ position: "absolute", top: 4, right: 4, p: 0.25 }} />
                <Box sx={{ fontSize: iconSize, mt: 0.5, mb: 0.5 }}>
                    {iconFor(doc.contentType ?? "", doc.title)}
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        width: "100%", textAlign: "center",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                    title={doc.title}
                >
                    {doc.title}
                </Typography>
            </Paper>
        </Tooltip>
    );
}

// helper: trigger one download via a hidden anchor
const triggerDownload = (url: string, suggestedName?: string) => {
    const a = document.createElement("a");
    a.href = url;
    if (suggestedName) a.download = suggestedName; // works for same-origin; cross-origin may ignore
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

export default function DocumentIconGallery({
    documents,
    dense,
    initialSelection = [],
    onDeleteSelected,
    onSelectionChange,
    hideDeleteConfirm,
    showDeleteButton,
}: Props) {
    const [selected, setSelected] = React.useState<Set<string>>(new Set(initialSelection));
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const userCanDelete = showDeleteButton ?? true;

    React.useEffect(() => {
        onSelectionChange?.(Array.from(selected));
    }, [selected, onSelectionChange]);

    const toggle = (id: string, multi: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (multi) {
                next.has(id) ? next.delete(id) : next.add(id);
            } else {
                if (prev.size === 1 && prev.has(id)) { next.clear(); }
                else { next.clear(); next.add(id); }
            }
            return next;
        });
    };

    const clearSel = () => setSelected(new Set());
    const ids = React.useMemo(() => Array.from(selected), [selected]);

    const handleDownload = () => {
        const urls = ids
            .map((id) => documents.find((x) => x.id === id))
            .filter(Boolean) as DocumentDetails[];

        urls.forEach((doc, i) => {
            setTimeout(() => triggerDownload(doc.downloadUrl, doc.title), i * 250);
        });
    };

    const confirmDelete = () => {
        if (hideDeleteConfirm) {
            doDelete();
        } else {
            setConfirmOpen(true);
        }
    };

    const doDelete = async () => {
        if (!onDeleteSelected) return;
        await onDeleteSelected(ids);
        clearSel();
        setConfirmOpen(false);
    };

    return (
        <Paper elevation={3} square={false} sx={{ p: 0.5, width: '100%', boxSizing: 'border-box' }}>
        <Stack spacing={1.25}>
            
                    <div className="section-header">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: { xs: "wrap", sm: "nowrap" },
                            gap: 1
                        }}
                    >
                        <Typography variant="h6">Documents</Typography>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: { xs: "flex-start", sm: "flex-end" },
                                alignItems: "center",
                                gap: 1,
                                width: { xs: "100%", sm: "auto" },
                                flexWrap: "wrap"
                            }}
                        >
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                disabled={!ids.length}
                                onClick={handleDownload}
                            >
                                Download
                            </Button>
                            {userCanDelete ? (
                                <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    disabled={!ids.length || !onDeleteSelected}
                                    onClick={confirmDelete}
                                >
                                    Delete
                                </Button>
                            ) : <></>}
                            </Box>
                        </Box>
                    </div>
                    
            <Divider sx={{ my: 2 }} />

            {/* Grid */}
            {documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No documents</Typography>
            ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(auto-fill, minmax(96px, 1fr))",
                                },
                                gap: 1,
                                justifyItems: "center",
                                alignItems: "center"
                            }}
                        >
                    {documents.map((doc) => (
                        <Box
                            key={doc.id}
                            onClick={(e) => toggle(doc.id, e.ctrlKey || e.metaKey)}
                        >
                            <DocTile
                                doc={doc}
                                selected={selected.has(doc.id)}
                                onToggle={() => { }}
                                dense={dense}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {/* Delete confirm */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Delete {ids.length} document{ids.length === 1 ? "" : "s"}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
            </Stack>
        </Paper>
    );
}
